import * as THREE from 'three'
import { SHADING_LIGHT, type ShadingRig } from './theme'

/**
 * Surfaces are shaded by a fixed three-plane axonometric rig rather than by
 * scene lights. Reasons:
 *  - the result is deterministic (no dependency on three's light unit changes),
 *  - flat planes stay perfectly flat (no falloff gradients across a desktop),
 *  - the multiplier is applied in perceptual sRGB space, so a white box reads as
 *    three clearly separated planes instead of three nearly identical ones.
 */
const vertexShader = /* glsl */ `
  varying vec3 vWorldNormal;

  void main() {
    // NOTE: assets must never be scaled non-uniformly; pass dimensions to the
    // geometry factories instead. That keeps this cheap normal transform exact.
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const fragmentShader = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uAmbient;
  uniform vec3 uKeyDir;
  uniform float uKeyIntensity;
  uniform vec3 uFillDir;
  uniform float uFillIntensity;
  uniform float uGroundBias;

  varying vec3 vWorldNormal;

  vec3 isoLinearToSrgb(vec3 c) {
    return mix(
      pow(c, vec3(0.41666)) * 1.055 - vec3(0.055),
      c * 12.92,
      vec3(lessThanEqual(c, vec3(0.0031308)))
    );
  }

  vec3 isoSrgbToLinear(vec3 c) {
    return mix(
      pow((c + vec3(0.055)) / 1.055, vec3(2.4)),
      c / 12.92,
      vec3(lessThanEqual(c, vec3(0.04045)))
    );
  }

  void main() {
    vec3 n = normalize(vWorldNormal);
    if (!gl_FrontFacing) n = -n;

    float key = max(dot(n, uKeyDir), 0.0);
    float fill = max(dot(n, uFillDir), 0.0);
    float delta = uAmbient
      + uKeyIntensity * key
      + uFillIntensity * fill
      - uGroundBias * max(-n.y, 0.0);

    /*
     * Signed shading: darken by a proportion of the base, lighten by a
     * proportion of the headroom. The darkening branch is exactly the old
     * multiply, so the light theme is bit-identical; the lightening branch is
     * what lets a charcoal surface still separate into three planes.
     */
    vec3 base = isoLinearToSrgb(uColor);
    vec3 shaded = delta < 0.0
      ? base * (1.0 + delta)
      : base + (vec3(1.0) - base) * delta;

    vec3 perceptual = clamp(shaded, 0.0, 1.0);
    gl_FragColor = vec4(isoSrgbToLinear(perceptual), uOpacity);

    #include <colorspace_fragment>
  }
`

/**
 * Shared uniform objects: every surface material references the *same* uniform
 * instances, so retuning the light rig at runtime touches one place.
 */
export const shadingUniforms = {
  uAmbient: { value: SHADING_LIGHT.ambient },
  uKeyDir: { value: SHADING_LIGHT.keyDir.clone() },
  uKeyIntensity: { value: SHADING_LIGHT.keyIntensity },
  uFillDir: { value: SHADING_LIGHT.fillDir.clone() },
  uFillIntensity: { value: SHADING_LIGHT.fillIntensity },
  uGroundBias: { value: SHADING_LIGHT.groundBias },
}

/** Swaps the whole scene's lighting rig — every material shares these objects. */
export function applyShadingRig(rig: ShadingRig): void {
  shadingUniforms.uAmbient.value = rig.ambient
  shadingUniforms.uKeyDir.value.copy(rig.keyDir)
  shadingUniforms.uKeyIntensity.value = rig.keyIntensity
  shadingUniforms.uFillDir.value.copy(rig.fillDir)
  shadingUniforms.uFillIntensity.value = rig.fillIntensity
  shadingUniforms.uGroundBias.value = rig.groundBias
}

export interface SurfaceParams {
  color: string
  opacity?: number
  transparent?: boolean
  depthWrite?: boolean
  side?: THREE.Side
}

const surfaceCache = new Map<string, THREE.ShaderMaterial>()

/**
 * Cached, shared surface material. Identical tones across the whole scene reuse
 * one material instance, which keeps GPU state changes low.
 */
export function getSurfaceMaterial(params: SurfaceParams): THREE.ShaderMaterial {
  const {
    color,
    opacity = 1,
    transparent = false,
    depthWrite = true,
    side = THREE.FrontSide,
  } = params
  const key = `${color}|${opacity}|${transparent}|${depthWrite}|${side}`
  const cached = surfaceCache.get(key)
  if (cached) return cached

  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uOpacity: { value: opacity },
      ...shadingUniforms,
    },
    transparent,
    depthWrite,
    side,
    // Push fills back a hair so the contour lines never z-fight with them.
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1,
  })
  material.name = `surface:${key}`
  surfaceCache.set(key, material)
  return material
}

const lineMaterialCache = new Map<string, LineMaterialLike>()

type LineMaterialLike = import('three/examples/jsm/lines/LineMaterial.js').LineMaterial

/**
 * Fat-line materials divide by `resolution.y` in the shader, so a material left
 * at (0, 0) renders absolutely nothing. Resolution is therefore owned globally
 * rather than per-instance: one place sets it, newly created materials inherit
 * it, and the exporter / thumbnail forge override it around their own renders.
 */
const outlineResolution = new THREE.Vector2(1, 1)

export function setOutlineResolution(width: number, height: number): void {
  if (width <= 0 || height <= 0) return
  outlineResolution.set(width, height)
  for (const material of lineMaterialCache.values()) {
    material.resolution.copy(outlineResolution)
  }
}

export function getOutlineResolution(): THREE.Vector2 {
  return outlineResolution
}

export function getOutlineMaterial(
  create: () => LineMaterialLike,
  color: string,
  width: number,
): LineMaterialLike {
  const key = `${color}|${width}`
  const cached = lineMaterialCache.get(key)
  if (cached) return cached
  const material = create()
  material.resolution.copy(outlineResolution)
  lineMaterialCache.set(key, material)
  return material
}

export function allOutlineMaterials(): LineMaterialLike[] {
  return [...lineMaterialCache.values()]
}

/**
 * Temporarily re-scales every contour material for an off-screen render at a
 * different pixel size, and returns the restore function. Used by the PNG
 * exporter and the thumbnail forge.
 */
export function overrideOutlineScale(pixelSize: number, widthScale: number): () => void {
  const saved = allOutlineMaterials().map((material) => ({
    material,
    resolution: material.resolution.clone(),
    linewidth: material.linewidth,
  }))
  for (const entry of saved) {
    entry.material.resolution.set(pixelSize, pixelSize)
    entry.material.linewidth = entry.linewidth * widthScale
  }
  return () => {
    for (const entry of saved) {
      entry.material.resolution.copy(entry.resolution)
      entry.material.linewidth = entry.linewidth
    }
  }
}
